import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
// Portal not used; render inline with fixed positioning
import { getCities, getManagers, getProductTypes, getHotelProductTypes, getProductDetailExtra, saveProductDetailExtra, saveSingleRequestField, deleteRequestField, insertRequestField, getRequestFieldInfo, getCurrencyCodes, copyTourProduct } from '../services/apiService';
import { getHotelBasicInfo } from '../services/productService';

// 키워드 칩 입력 컴포넌트: Enter/쉼표로 구분, 백스페이스로 마지막 삭제, X로 개별 삭제
const KeywordsInput = ({ value = '', onChange, placeholder = '' }) => {
  const [input, setInput] = useState('');
  const tags = (value || '').split(',').map(s => s.trim()).filter(Boolean);

  const emit = (nextTags) => {
    const str = nextTags.join(',');
    onChange && onChange(str);
  };

  const addTag = (raw) => {
    const tag = (raw || '').trim();
    if (!tag) return;
    if (tags.includes(tag)) {
      setInput('');
      return;
    }
    emit([...tags, tag]);
    setInput('');
  };

  const removeAt = (idx) => {
    const next = tags.filter((_, i) => i !== idx);
    emit(next);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag(input);
    } else if (e.key === 'Backspace' && !input && tags.length > 0) {
      // 입력이 비어있고 백스페이스면 마지막 태그 삭제
      e.preventDefault();
      removeAt(tags.length - 1);
    }
  };

  return (
    <div className="block w-full px-2 py-1.5 bg-white focus-within:ring-1 focus-within:ring-blue-500 focus-within:border-blue-500">
      <div className="flex flex-wrap items-center gap-1">
        {tags.map((t, idx) => (
          <span key={`${t}-${idx}`} className="inline-flex items-center bg-blue-50 text-blue-700 text-xs px-2 py-1 rounded-full">
            {t}
            <button
              type="button"
              aria-label="remove keyword"
              className="ml-1 text-blue-600 hover:text-blue-800"
              onClick={() => removeAt(idx)}
            >
              ×
            </button>
          </span>
        ))}
        <input
          id="키워드-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={tags.length === 0 ? placeholder : ''}
          className="flex-1 min-w-[120px] px-1 py-1 focus:outline-none text-sm"
        />
      </div>
    </div>
  );
};

const ProductSettingsModal = ({ product, onClose, onSave, loading, onPreviewRefresh, hideBasicCity = false, hideBasicDuration = false, hideDetailTab = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState(product);
  const [activeTab, setActiveTab] = useState('basic'); // 'basic' | 'detail'
  const [detailState, setDetailState] = useState({ loading: false, error: '', detail: null, requestFields: [] });
  const [savingDetail, setSavingDetail] = useState(false);
  // Auto-save toast message
  const [autoSaveMsg, setAutoSaveMsg] = useState('');
  const [hotelBasicLoading, setHotelBasicLoading] = useState(false);
  const [hotelBasic, setHotelBasic] = useState(null);
  // 서버에 저장된 최신 상세 설정 스냅샷 (초기 로드/저장 성공 시 갱신)
  const lastSavedDetailRef = useRef(null);
  const [dropdowns, setDropdowns] = useState({});
  const [managerOptions, setManagerOptions] = useState([]); // 코드명 배열
  const [productTypeOptions, setProductTypeOptions] = useState([]); // 코드명 배열
  // Copy product state
  const [currencyOptions, setCurrencyOptions] = useState([]); // [{코드, 코드명}]
  const [copyForm, setCopyForm] = useState({ name: '', currencyCode: '058001', duration: '', durationType: '012002' });
  const [copyLoading, setCopyLoading] = useState(false);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [currencyLoading, setCurrencyLoading] = useState(false);
  const [copyErrors, setCopyErrors] = useState({ name: '', duration: '', currencyCode: '', durationType: '' });
  const nameInputRef = useRef(null);
  const [navigateChoice, setNavigateChoice] = useState('none'); // 'none' | 'detail' | 'price'
  // Backdrop mouse tracking to avoid closing on drag from inside to outside
  const backdropMouseDownRef = useRef(false);
  // Enter/Exit animation state
  const [mountedVisible, setMountedVisible] = useState(false);
  const [animatingOut, setAnimatingOut] = useState(false);
  // Ensure DOM is ready for portal target
  const [hasDOM, setHasDOM] = useState(false);

  // 부모에게 미리보기 새로고침을 알림
  const refreshPreview = () => {
    try {
      if (typeof onPreviewRefresh === 'function') onPreviewRefresh();
    } catch (_) {}
  };

  useEffect(() => {
    // Trigger enter animation on mount
    const id = requestAnimationFrame(() => setMountedVisible(true));
    const domReady = typeof document !== 'undefined' && !!document.body;
    setHasDOM(domReady);
    return () => {
      cancelAnimationFrame(id);
    };
  }, []);

  // Open copy modal and fetch currency codes on demand
  const openCopyModal = async () => {
    try {
      console.debug('openCopyModal: fetching currencies and opening copy modal');
      setCurrencyLoading(true);
      const list = await getCurrencyCodes();
      const arr = Array.isArray(list) ? list : [];
      setCurrencyOptions(arr);
      if (arr.length && !arr.some((c) => String(c.코드) === String(copyForm.currencyCode))) {
        setCopyForm((prev) => ({ ...prev, currencyCode: String(arr[0].코드) }));
      }
      setShowCopyModal(true);
    } catch (e) {
      setCurrencyOptions([]);
      setShowCopyModal(true); // show modal even if list failed, with empty options
    } finally {
      setCurrencyLoading(false);
    }
  };

  // Autofocus and keyboard shortcuts for copy modal
  useEffect(() => {
    if (!showCopyModal) return;
    // Focus name input shortly after open
    const tid = window.setTimeout(() => {
      try { nameInputRef.current && nameInputRef.current.focus(); } catch {}
    }, 0);
    const onKeyDown = (e) => {
      if (!showCopyModal) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        setShowCopyModal(false);
      } else if (e.key === 'Enter') {
        // Submit only when focus is within the modal (best effort)
        handleCopySubmit();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      window.clearTimeout(tid);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [showCopyModal]);

  const validateCopyForm = () => {
    const errs = { name: '', duration: '', currencyCode: '', durationType: '' };
    const name = String(copyForm.name || '').trim();
    if (!name) errs.name = '새 상품명을 입력하세요.';
    const durStr = String(copyForm.duration || '').trim();
    if (!/^[0-9]+$/.test(durStr)) errs.duration = '소요시간은 숫자만 입력하세요.';
    else if (Number(durStr) <= 0) errs.duration = '소요시간은 1 이상이어야 합니다.';
    if (!copyForm.currencyCode) errs.currencyCode = '통화코드를 선택하세요.';
    if (!copyForm.durationType) errs.durationType = '소요시간타입을 선택하세요.';
    setCopyErrors(errs);
    // return validity
    return Object.values(errs).every((v) => !v);
  };

  const handleCopySubmit = async () => {
    if (!validateCopyForm()) return;
    if (!product?.상품번호) { alert('원본 상품번호가 없습니다.'); return; }
    try {
      setCopyLoading(true);
      const res = await copyTourProduct({
        originalProductNo: product.상품번호,
        productName: String(copyForm.name || '').trim(),
        duration: Number(copyForm.duration),
        durationType: copyForm.durationType,
        currencyCode: copyForm.currencyCode,
      });
      const dataTable = Array.isArray(res?.table) ? res.table : Array.isArray(res?.Table) ? res.Table : [];
      const first = dataTable[0] || {};
      const newNo = first.상품번호 || first.새상품번호 || first.ProductNo || null;
      alert(`상품 복사가 완료되었습니다.${newNo ? `\n새 상품번호: ${newNo}` : ''}`);
      setShowCopyModal(false);
      try { refreshPreview(); } catch {}
      if (newNo && navigateChoice === 'detail') {
        navigate(`/detail-settings/${newNo}`);
      } else if (newNo && navigateChoice === 'price') {
        navigate(`/price-stock/${newNo}`);
      } else {
        window.location.reload();
      }
    } catch (e) {
      console.error('상품 복사 실패', e);
      alert('상품 복사에 실패했습니다. 입력값을 확인하거나 잠시 후 다시 시도해주세요.');
    } finally {
      setCopyLoading(false);
    }
  };

  const requestClose = () => {
    // Play exit animation then invoke onClose
    setAnimatingOut(true);
    setMountedVisible(false);
    window.setTimeout(() => {
      // 저장 후 모달을 닫을 때, 상위에서 콘텐츠 메인 리스트를 갱신하도록 트리거
      if (typeof onPreviewRefresh === 'function' && product?.상품번호) {
        try {
          onPreviewRefresh(product.상품번호);
        } catch (e) {
          // no-op: refresh 실패는 모달 닫힘을 막지 않음
        }
      }
      onClose && onClose();
    }, 200); // match duration classes below
  };

  // Sample dropdown options - replace with actual data (도시는 API로 대체)
  const dropdownOptions = {
    '도시': [],
    '상품타입': productTypeOptions,
    '상품담당': managerOptions,
    '예약담당': managerOptions,
    '원가통화': Array.isArray(currencyOptions) ? currencyOptions.map((c) => c.코드명) : [],
  };

  useEffect(() => {
    setFormData(product);
  }, [product]);

  const isHotel = (
    String(product?.상품종류 || '').includes('호텔') ||
    String(product?.상품종류코드 || '') === '001001'
  );

  // Load 호텔 기초설정 값 when opening the basic tab (호텔 한정)
  useEffect(() => {
    let mounted = true;
    const loadHotelBasic = async () => {
      try {
        if (!isHotel) return;
        if (!product?.상품번호) return;
        if (activeTab !== 'basic') return;
        setHotelBasicLoading(true);
        const row = await getHotelBasicInfo(product.상품번호).catch(() => null);
        if (!mounted) return;
        setHotelBasic(row);
        if (row) {
          setFormData(prev => ({
            ...prev,
            상품명: row.내부상품명 ?? prev.상품명,
            상품코드: row.상품코드 ?? prev.상품코드,
            // 호텔은 카테고리 우선, 투어는 도시 사용
            도시: isHotel ? (row.카테고리 ?? row.도시 ?? prev.도시) : (row.도시 ?? prev.도시),
            상품타입: row.상품타입 ?? prev.상품타입,
            상품담당: row.상품담당 ?? prev.상품담당,
            예약담당: row.예약담당 ?? prev.예약담당,
            체크인시간: row.체크인시간 ?? prev.체크인시간,
            체크아웃시간: row.체크아웃시간 ?? prev.체크아웃시간,
            원가통화: row.원가통화 ?? prev.원가통화,
          }));
        }
      } finally {
        setHotelBasicLoading(false);
      }
    };
    loadHotelBasic();
    return () => { mounted = false; };
  }, [product, activeTab, isHotel]);

  // Load currency list for 원가통화 dropdown when basic tab opens (호텔 한정)
  useEffect(() => {
    let mounted = true;
    const ensureCurrencies = async () => {
      if (!isHotel) return;
      if (activeTab !== 'basic') return;
      try {
        setCurrencyLoading(true);
        const list = await getCurrencyCodes();
        if (!mounted) return;
        setCurrencyOptions(Array.isArray(list) ? list : []);
      } catch (e) {
        if (!mounted) return;
        setCurrencyOptions([]);
      } finally {
        setCurrencyLoading(false);
      }
    };
    ensureCurrencies();
    return () => { mounted = false; };
  }, [activeTab, isHotel]);

  // Load 상세 정보 when switching to detail/request tab (once per product)
  useEffect(() => {
    let mounted = true;
    const loadDetail = async () => {
      if (!product?.상품번호) return;
      try {
        setDetailState(prev => ({ ...prev, loading: true, error: '' }));
        const res = await getProductDetailExtra(product.상품번호);
        if (!mounted) return;
        const rawDetail = res.detail || {};
        const method = String(rawDetail.예약확정방식 || '');
        const unit = String(rawDetail.예약마감시간타입 || '');
        const localPay = Number(rawDetail.현지지불코드);
        const depositType = String(rawDetail.신청금구분 || '');
        const leadOnly = String(rawDetail.대표승객여부 ?? '');
        const passportCopyNeed = !!rawDetail.여권사본필요여부;
        const passportIssueRequired = !!rawDetail.여권발급일필수여부;
        const ageGroupUsed = rawDetail.연령구분여부 !== false; // 기본값 true
        const settleBase = String(rawDetail.완납기준일 || '');
        const settleDue = Number(rawDetail.완납시한 ?? 0);
        const normalizedDetail = {
          ...rawDetail,
          예약확정방식: ['010001','010002'].includes(method) ? method : '010001',
          예약마감시간타입: ['012001','012002'].includes(unit) ? unit : '012002',
          현지지불코드: (localPay === 0 || localPay === 1) ? localPay : 0,
          신청금구분: ['004001','004002','004003','004004','004999'].includes(depositType) ? depositType : '004999',
          대표승객여부: ['0','1'].includes(leadOnly) ? leadOnly : '1',
          여권사본필요여부: passportCopyNeed,
          여권발급일필수여부: passportIssueRequired,
          연령구분여부: ageGroupUsed,
          완납기준일: ['205001','205002','205003'].includes(settleBase) ? settleBase : '205001',
          완납시한: Number.isFinite(settleDue) ? Math.max(0, settleDue) : 0,
          // Age ranges normalization
          연령유아시작: 0,
          연령유아끝: Math.max(0, Number(rawDetail.연령유아끝 ?? 3)),
          연령소아시작: Math.max(0, Number(rawDetail.연령소아시작 ?? Number(rawDetail.연령유아끝 ?? 3))),
          연령소아끝: Math.max(0, Number(rawDetail.연령소아끝 ?? 12)),
          연령성인시작: Math.max(0, Number(rawDetail.연령성인시작 ?? Number(rawDetail.연령소아끝 ?? 12)))
        };
        // Ensure sync relationships after initial normalization
        if (normalizedDetail.연령소아시작 !== normalizedDetail.연령유아끝) {
          normalizedDetail.연령소아시작 = normalizedDetail.연령유아끝;
        }
        if (normalizedDetail.연령성인시작 !== normalizedDetail.연령소아끝) {
          normalizedDetail.연령성인시작 = normalizedDetail.연령소아끝;
        }
        const mapType = (t) => {
          const s = String(t || '').toLowerCase();
          if (s === 'list' || s === 'list ') return '리스트';
          if (s === 'text') return '텍스트';
          if (s === 'checkbox') return '체크박스';
          return t;
        };
        const mappedRequestFields = (res.requestFields || []).map((rf) => ({
          ...rf,
          데이터타입: mapType(rf.데이터타입),
        }));
        setDetailState({ loading: false, error: '', detail: normalizedDetail, requestFields: mappedRequestFields });
        // 리스트 타입 필드 옵션값 보강
        try {
          const listFields = mappedRequestFields.filter(rf => rf.데이터타입 === '리스트' && rf.필드번호);
          if (listFields.length) {
            const results = await Promise.allSettled(listFields.map(rf => getRequestFieldInfo({ fieldNo: rf.필드번호 })));
            const optionsByFieldNo = {};
            results.forEach((res, i) => {
              if (res.status === 'fulfilled') {
                const rows = Array.isArray(res.value) ? res.value : (res.value?.table || []);
                const row = rows[0];
                const raw = row?.리스트값 || '';
                optionsByFieldNo[listFields[i].필드번호] = raw ? String(raw).split('\n').filter(Boolean) : [];
              }
            });
            setDetailState(prev => ({
              ...prev,
              requestFields: (prev.requestFields || []).map(rf => (
                rf.데이터타입 === '리스트' && rf.필드번호 && optionsByFieldNo[rf.필드번호]
                  ? { ...rf, 옵션목록: optionsByFieldNo[rf.필드번호] }
                  : rf
              )),
            }));
          }
        } catch (e) {
          console.warn('리스트 옵션값 보강 실패', e);
        }
        lastSavedDetailRef.current = normalizedDetail;
      } catch (e) {
        if (!mounted) return;
        // 신규 상품 등으로 상세 정보가 아직 없을 수 있으므로, 에러 대신 안전한 기본값으로 초기화한다.
        const normalizedDetail = {
          예약확정방식: '010001',
          예약마감시간타입: '012002',
          현지지불코드: 0,
          신청금구분: '004999',
          대표승객여부: '1',
          여권사본필요여부: false,
          여권발급일필수여부: false,
          연령구분여부: true,
          완납기준일: '205001',
          완납시한: 0,
          연령유아시작: 0,
          연령유아끝: 3,
          연령소아시작: 3,
          연령소아끝: 12,
          연령성인시작: 12,
        };
        setDetailState(prev => ({ ...prev, loading: false, error: '', detail: normalizedDetail }));
        lastSavedDetailRef.current = normalizedDetail;
      }
    };
    if ((activeTab === 'detail' || activeTab === 'request') && detailState.detail == null) {
      loadDetail();
    }
    return () => { mounted = false; };
  }, [activeTab, product]);

  // 요청 탭으로 전환 시 최신 상세 설정만 새로 가져와 스냅샷(ref) 갱신 (드래프트는 덮어쓰지 않음)
  useEffect(() => {
    const refreshSnapshot = async () => {
      try {
        if (!product?.상품번호) return;
        const res = await getProductDetailExtra(product.상품번호);
        const rawDetail = res.detail || {};
        const method = String(rawDetail.예약확정방식 || '');
        const unit = String(rawDetail.예약마감시간타입 || '');
        const localPay = Number(rawDetail.현지지불코드);
        const depositType = String(rawDetail.신청금구분 || '');
        const leadOnly = String(rawDetail.대표승객여부 ?? '');
        const passportCopyNeed = !!rawDetail.여권사본필요여부;
        const passportIssueRequired = !!rawDetail.여권발급일필수여부;
        const ageGroupUsed = rawDetail.연령구분여부 !== false;
        const settleBase = String(rawDetail.완납기준일 || '');
        const settleDue = Number(rawDetail.완납시한 ?? 0);
        const normalizedDetail = {
          ...rawDetail,
          예약확정방식: ['010001','010002'].includes(method) ? method : '010001',
          예약마감시간타입: ['012001','012002'].includes(unit) ? unit : '012002',
          현지지불코드: (localPay === 0 || localPay === 1) ? localPay : 0,
          신청금구분: ['004001','004002','004003','004004','004999'].includes(depositType) ? depositType : '004999',
          대표승객여부: ['0','1'].includes(leadOnly) ? leadOnly : '1',
          여권사본필요여부: passportCopyNeed,
          여권발급일필수여부: passportIssueRequired,
          연령구분여부: ageGroupUsed,
          완납기준일: ['205001','205002','205003'].includes(settleBase) ? settleBase : '205001',
          완납시한: Number.isFinite(settleDue) ? Math.max(0, settleDue) : 0,
        };
        lastSavedDetailRef.current = normalizedDetail;
      } catch (e) {
        // 스냅샷 새로고침 실패는 치명적이지 않음
        console.warn('최신 상세 설정 스냅샷 갱신 실패', e);
      }
    };
    if (activeTab === 'request') {
      refreshSnapshot();
    }
  }, [activeTab, product]);

  // 저장 이후 부모가 최신 product를 다시 넘겨주면, detail/request 탭 내용도 즉시 재조회하여 최신 상태로 동기화
  useEffect(() => {
    let mounted = true;
    const refreshDetailOnProductChange = async () => {
      try {
        if (!product?.상품번호) return;
        if (!(activeTab === 'detail' || activeTab === 'request')) return;
        setDetailState(prev => ({ ...prev, loading: true }));
        const res = await getProductDetailExtra(product.상품번호);
        if (!mounted) return;
        const rawDetail = res.detail || {};
        const method = String(rawDetail.예약확정방식 || '');
        const unit = String(rawDetail.예약마감시간타입 || '');
        const localPay = Number(rawDetail.현지지불코드);
        const depositType = String(rawDetail.신청금구분 || '');
        const leadOnly = String(rawDetail.대표승객여부 ?? '');
        const passportCopyNeed = !!rawDetail.여권사본필요여부;
        const passportIssueRequired = !!rawDetail.여권발급일필수여부;
        const ageGroupUsed = rawDetail.연령구분여부 !== false;
        const settleBase = String(rawDetail.완납기준일 || '');
        const settleDue = Number(rawDetail.완납시한 ?? 0);
        const normalizedDetail = {
          ...rawDetail,
          예약확정방식: ['010001','010002'].includes(method) ? method : '010001',
          예약마감시간타입: ['012001','012002'].includes(unit) ? unit : '012002',
          현지지불코드: (localPay === 0 || localPay === 1) ? localPay : 0,
          신청금구분: ['004001','004002','004003','004004','004999'].includes(depositType) ? depositType : '004999',
          대표승객여부: ['0','1'].includes(leadOnly) ? leadOnly : '1',
          여권사본필요여부: passportCopyNeed,
          여권발급일필수여부: passportIssueRequired,
          연령구분여부: ageGroupUsed,
          완납기준일: ['205001','205002','205003'].includes(settleBase) ? settleBase : '205001',
          완납시한: Number.isFinite(settleDue) ? Math.max(0, settleDue) : 0,
        };
        const mapType = (t) => {
          const s = String(t || '').toLowerCase();
          if (s === 'list' || s === 'list ') return '리스트';
          if (s === 'text') return '텍스트';
          if (s === 'checkbox') return '체크박스';
          return t;
        };
        const mappedRequestFields = (res.requestFields || []).map((rf) => ({
          ...rf,
          데이터타입: mapType(rf.데이터타입),
        }));
        setDetailState({ loading: false, error: '', detail: normalizedDetail, requestFields: mappedRequestFields });
        lastSavedDetailRef.current = normalizedDetail;
      } catch (e) {
        if (!mounted) return;
        setDetailState(prev => ({ ...prev, loading: false }));
      }
    };
    // product가 갱신될 때마다 시도 (탭이 detail/request일 때만)
    refreshDetailOnProductChange();
    return () => { mounted = false; };
  }, [product, activeTab]);

  // 담당자 목록 로드 (초성 검색 불필요, 코드명만 표시)
  useEffect(() => {
    let mounted = true;
    const fetchManagers = async () => {
      try {
        const list = await getManagers(); // [{코드, 코드명}]
        if (!mounted) return;
        setManagerOptions(Array.isArray(list) ? list.map((m) => m.코드명) : []);
      } catch (e) {
        if (!mounted) return;
        setManagerOptions([]);
      }
    };
    fetchManagers();
    return () => { mounted = false; };
  }, []);

  // 상품타입 목록 로드: 호텔은 호텔상품타입, 그 외(투어 등)는 기존 투어상품타입
  useEffect(() => {
    let mounted = true;
    const fetchProductTypes = async () => {
      try {
        const list = await (isHotel ? getHotelProductTypes() : getProductTypes()); // [{코드, 코드명}]
        if (!mounted) return;
        setProductTypeOptions(Array.isArray(list) ? list.map((t) => t.코드명) : []);
      } catch (e) {
        if (!mounted) return;
        setProductTypeOptions([]);
      }
    };
    fetchProductTypes();
    return () => { mounted = false; };
  }, [isHotel]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleDropdownChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    setDropdowns(prev => ({ ...prev, [name]: false }));
  };

  // 간단한 저장 알림 표시
  const showAutoSaved = (msg = '변경사항이 저장되었습니다.') => {
    try {
      setAutoSaveMsg(msg);
      window.clearTimeout(showAutoSaved._tid);
      showAutoSaved._tid = window.setTimeout(() => setAutoSaveMsg(''), 1400);
      // Fallback alert to ensure visibility if toast is not mounted yet
      try { if (typeof window !== 'undefined' && window?.Notification == null) { /* noop */ } } catch {}
      try { window.alert && window.alert(msg); } catch {}
    } catch {}
  };

  // 예약 필드 단건 저장 호출
  const persistRequestField = async (field) => {
    try {
      const productTypeName = product?.상품종류 || product?.상품타입 || detailState.detail?.상품종류 || '';
      if (!field.필드번호) {
        // 최초 저장: Insert (@필드번호=NULL)
        await insertRequestField({ productTypeName, field });
        showAutoSaved();
        // 서버 반영 후 최신 목록으로 동기화 (필드번호 획득 목적)
        const res = await getProductDetailExtra(product.상품번호);
        const mapType = (t) => {
          const s = String(t || '').toLowerCase();
          if (s === 'list' || s === 'list ') return '리스트';
          if (s === 'text') return '텍스트';
          if (s === 'checkbox') return '체크박스';
          return t;
        };
        const mapped = (res.requestFields || []).map(rf => ({ ...rf, 데이터타입: mapType(rf.데이터타입) }));
        setDetailState(prev => ({ ...prev, requestFields: mapped.length ? mapped : prev.requestFields }));
        // Insert 후에도 리스트 타입 옵션값 보강
        try {
          const listFields = mapped.filter(rf => rf.데이터타입 === '리스트' && rf.필드번호);
          if (listFields.length) {
            const results = await Promise.allSettled(listFields.map(rf => getRequestFieldInfo({ fieldNo: rf.필드번호 })));
            const optionsByFieldNo = {};
            results.forEach((res, i) => {
              if (res.status === 'fulfilled') {
                const rows = Array.isArray(res.value) ? res.value : (res.value?.table || []);
                const row = rows[0];
                const raw = row?.리스트값 || '';
                optionsByFieldNo[listFields[i].필드번호] = raw ? String(raw).split('\n').filter(Boolean) : [];
              }
            });
            setDetailState(prev => ({
              ...prev,
              requestFields: (prev.requestFields || []).map(rf => (
                rf.데이터타입 === '리스트' && rf.필드번호 && optionsByFieldNo[rf.필드번호]
                  ? { ...rf, 옵션목록: optionsByFieldNo[rf.필드번호] }
                  : rf
              )),
            }));
          }
        } catch (e) {
          console.warn('Insert 후 리스트 옵션값 보강 실패', e);
        }
      } else {
        // 이후 변경: Update
        await saveSingleRequestField({ productTypeName, field });
        showAutoSaved();
      }
    } catch (e) {
      console.error('persistRequestField failed', e);
    }
  };

  const toggleDropdown = (name) => {
    setDropdowns(prev => ({
      ...prev,
      [name]: !prev[name]
    }));
  };

  if (loading) {
    if (!hasDOM) return null;
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-[200]">
        <div 
          className="fixed top-0 right-0 h-full w-1/3 bg-white shadow-lg z-[210] p-6 flex justify-center items-center"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  if (!product) return null;
  // portal ready check
  if (!hasDOM) return null;

  return (
    <div
      className={`fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-[200] transition-opacity duration-200 ${animatingOut ? 'ease-in opacity-0' : 'ease-out'} ${mountedVisible && !animatingOut ? 'opacity-100' : 'opacity-0'}`}
      onMouseDown={(e) => {
        // Only mark if the initial press was on the backdrop itself
        backdropMouseDownRef.current = (e.target === e.currentTarget);
      }}
      onMouseUp={(e) => {
        // Close only if both down and up occurred on the backdrop
        if (backdropMouseDownRef.current && e.target === e.currentTarget) {
          requestClose();
        }
        backdropMouseDownRef.current = false;
      }}
    >
      <div 
        className={`fixed top-0 right-0 h-full w-full sm:w-[580px] md:w-[680px] lg:w-[780px] bg-white shadow-xl border-l border-gray-200 sm:rounded-l-2xl rounded-l-lg z-[210] p-4 overflow-y-auto transform transition-transform duration-200 ${animatingOut ? 'ease-in translate-x-full' : 'ease-out'} ${mountedVisible && !animatingOut ? 'translate-x-0' : 'translate-x-full'}`}
        onMouseDown={(e) => e.stopPropagation()}
        onMouseUp={(e) => e.stopPropagation()}
      >
        {/* Header & Tabs */}
        <div className="sticky top-0 z-10 bg-white mb-4 pb-2">
          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              
              <div>
                <h2 className="text-lg font-semibold text-gray-900">기본 설정</h2>
                              </div>
            </div>
            <button
              type="button"
              aria-label="닫기"
              onClick={requestClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-5 w-5">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
          <div className="inline-flex w-full rounded-md border border-gray-200 bg-gray-50 overflow-hidden text-[11px]" role="tablist" aria-label="기본 설정 탭">
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'basic'}
              onClick={() => setActiveTab('basic')}
              className={`flex-1 px-3 py-2.5 border-r border-gray-200 text-center transition-colors ${
                activeTab === 'basic' 
                  ? 'bg-gray-700 text-[13px] text-white font-semibold' 
                  : 'bg-gray-50 text-[13px] text-gray-800 hover:bg-gray-100'
              }`}
            >
              기초 설정
              <span className="block mt-0.5 text-[10px] font-normal opacity-80">상품 기본 정보</span>
            </button>
            {!hideDetailTab && (
              <button
                type="button"
                role="tab"
                aria-selected={activeTab === 'detail'}
                onClick={() => setActiveTab('detail')}
                className={`flex-1 px-3 py-2.5 border-r border-gray-200 text-center transition-colors ${
                  activeTab === 'detail' 
                    ? 'bg-gray-700 text-[13px] text-white font-semibold' 
                    : 'bg-gray-50 text-[13px] text-gray-800 hover:bg-gray-100'
                }`}
              >
                웹 예약 설정
                <span className="block mt-0.5 text-[10px] font-normal opacity-80">예약/결제 옵션</span>
              </button>
            )}
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'request'}
              onClick={() => setActiveTab('request')}
              className={`flex-1 px-3 py-2.5 text-center transition-colors ${
                activeTab === 'request' 
                  ? 'bg-gray-700 text-[13px] text-white font-semibold' 
                  : 'bg-gray-50 text-[13px] text-gray-800 hover:bg-gray-100'
              }`}
            >
              웹 예약 추가 입력 정보 설정
              <span className="block mt-0.5 text-[10px] font-normal opacity-80">예약시 받을 정보</span>
            </button>
          </div>
        </div>

        {activeTab === 'basic' && (
          <div className="space-y-4">
            {/* 기본 정보 섹션 */}
            <div>
              <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-100">
                <div className="w-1 h-5 bg-blue-500 rounded-full"></div>
                <div className="text-sm font-semibold text-gray-800">기본 정보</div>
              </div>
              <div className="pl-3 space-y-2 text-sm">
                <FieldRow 
                  label="상품명"
                  rightAction={<IconButton ariaLabel="상품명 편집" onClick={() => document.getElementById('상품명-input')?.focus()} />}
                >
                  <InputField id="상품명-input" name="상품명" value={formData.상품명 || ''} onChange={handleChange} placeholder="상품명을 입력하세요" />
                </FieldRow>

                <FieldRow 
                  label="상품코드"
                  rightAction={<IconButton ariaLabel="상품코드 편집" onClick={() => document.getElementById('상품코드-input')?.focus()} />}
                >
                  <InputField id="상품코드-input" name="상품코드" value={formData.상품코드 || ''} onChange={handleChange} placeholder="상품코드를 입력하세요" />
                </FieldRow>

                {!hideBasicCity && (
                  <FieldRow label={isHotel ? "카테고리" : "도시"}>
                    <CitySelector
                      value={formData.도시}
                      isOpen={!!dropdowns['도시']}
                      onToggle={() => toggleDropdown('도시')}
                      onChange={(value) => handleDropdownChange('도시', value)}
                    />
                  </FieldRow>
                )}

                <FieldRow label="상품타입">
                  <DropdownField 
                    name="상품타입" 
                    value={formData.상품타입} 
                    options={dropdownOptions['상품타입']} 
                    isOpen={dropdowns['상품타입']} 
                    onToggle={() => toggleDropdown('상품타입')} 
                    onChange={(value) => handleDropdownChange('상품타입', value)} 
                  />
                </FieldRow>
                {isHotel && (
                  <>
                    <FieldRow label="체크인/체크아웃">
                      <div className="px-3 py-2 flex items-center gap-4">
                        {hotelBasicLoading ? (
                          <span className="text-gray-500">불러오는 중...</span>
                        ) : (
                          <>
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-gray-600">체크인</span>
                              <input
                                type="time"
                                name="체크인시간"
                                value={formData.체크인시간 || ''}
                                onChange={handleChange}
                                className="block w-36 px-3 py-2 border rounded"
                              />
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-gray-600">체크아웃</span>
                              <input
                                type="time"
                                name="체크아웃시간"
                                value={formData.체크아웃시간 || ''}
                                onChange={handleChange}
                                className="block w-36 px-3 py-2 border rounded"
                              />
                            </div>
                          </>
                        )}
                      </div>
                    </FieldRow>
                    <FieldRow label="원가통화">
                      <div className="space-y-1">
                        <div className="px-3 py-2 text-gray-700 rounded cursor-not-allowed">
                          {formData.원가통화 || '선택하세요'}
                        </div>
                       
                      </div>
                    </FieldRow>
                  </>
                )}
              </div>
            </div>

            {/* 담당자 정보 섹션 */}
            <div className="mt-6">
              <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-100">
                <div className="w-1 h-5 bg-emerald-500 rounded-full"></div>
                <div className="text-sm font-semibold text-gray-800">담당자 정보</div>
              </div>
              <div className="pl-3 space-y-2 text-sm">
                <FieldRow label="상품담당">
                  <DropdownField 
                    name="상품담당" 
                    value={formData.상품담당} 
                    options={dropdownOptions['상품담당']} 
                    isOpen={dropdowns['상품담당']} 
                    onToggle={() => toggleDropdown('상품담당')} 
                    onChange={(value) => handleDropdownChange('상품담당', value)} 
                  />
                </FieldRow>

                <FieldRow label="예약담당">
                  <DropdownField 
                    name="예약담당" 
                    value={formData.예약담당} 
                    options={dropdownOptions['예약담당']} 
                    isOpen={dropdowns['예약담당']} 
                    onToggle={() => toggleDropdown('예약담당')} 
                    onChange={(value) => handleDropdownChange('예약담당', value)} 
                  />
                </FieldRow>
              </div>
            </div>

            {/* 추가 정보 섹션 */}
            <div className="mt-6">
              <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-100">
                <div className="w-1 h-5 bg-indigo-500 rounded-full"></div>
                <div className="text-sm font-semibold text-gray-800">추가 정보</div>
              </div>
              <div className="pl-3 space-y-2 text-sm">
                {!isHotel && !hideBasicDuration && (
                  <FieldRow label="소요시간">
                    <div className="px-3 py-2 text-gray-700">
                      {formData.소요시간 || '-'} {formData.소요단위 || formData.소요 || ''}
                    </div>
                  </FieldRow>
                )}

                <FieldRow 
                  label="키워드"
                  rightAction={<IconButton ariaLabel="키워드 편집" onClick={() => document.getElementById('키워드-input')?.focus()} />}
                >
                  <KeywordsInput 
                    value={formData.키워드 || ''}
                    onChange={(val) => setFormData(prev => ({ ...prev, 키워드: val }))}
                    placeholder="키워드 입력 후 Enter"
                  />
                </FieldRow>
                <FieldRow label="상품뱃지">
                  <InputField
                    id="상품뱃지-input"
                    name="상품뱃지"
                    value={formData.상품뱃지 || ''}
                    onChange={handleChange}
                    placeholder="상품 목록에 표시할 뱃지 텍스트"
                  />
                </FieldRow>
                {/* 상품 복사 버튼 제거 - 하단으로 이동 */}
                
              </div>
            </div>
          </div>
        )}

        {!hideDetailTab && activeTab === 'detail' && (
          <div className="space-y-4">
            {detailState.loading && <div className="text-sm text-gray-500">상세 정보를 불러오는 중...</div>}
            {detailState.error && <div className="text-sm text-red-500">{detailState.error}</div>}
            {!detailState.loading && (
              <>
                {/* 예약 설정 섹션 */}
                <div>
                  <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-100">
                    <div className="w-1 h-5 bg-blue-500 rounded-full"></div>
                    <div className="text-sm font-semibold text-gray-800">예약 설정</div>
                  </div>
                  <div className="pl-3 space-y-2 text-sm">
                    <FieldRow label="대기예약허용">
                      <Switch
                        checked={!!detailState.detail?.대기예약허용여부}
                        onChange={(val) => setDetailState(prev => ({ ...prev, detail: { ...(prev.detail||{}), 대기예약허용여부: val } }))}
                      />
                    </FieldRow>
                    <FieldRow label="예약확정방식">
                      <div className="flex items-center space-x-6" role="radiogroup" aria-label="예약확정방식">
                        <label className="inline-flex items-center space-x-2 cursor-pointer">
                          <div className="relative">
                            <input
                              type="radio"
                              className="sr-only peer"
                              name="confirmMethod"
                              value="010001"
                              checked={detailState.detail?.예약확정방식 === '010001'}
                              onChange={() => setDetailState(prev => ({ ...prev, detail: { ...(prev.detail||{}), 예약확정방식: '010001' } }))}
                            />
                            <div className="w-5 h-5 border border-gray-300 rounded-full bg-white peer-checked:border-blue-500 transition-all"></div>
                            <div className="absolute top-1 left-1 w-3 h-3 rounded-full bg-blue-500 opacity-0 peer-checked:opacity-100 transition-opacity"></div>
                          </div>
                          <span className="text-sm font-medium text-gray-700">자동확정</span>
                        </label>
                        <label className="inline-flex items-center space-x-2 cursor-pointer">
                          <div className="relative">
                            <input
                              type="radio"
                              className="sr-only peer"
                              name="confirmMethod"
                              value="010002"
                              checked={detailState.detail?.예약확정방식 === '010002'}
                              onChange={() => setDetailState(prev => ({ ...prev, detail: { ...(prev.detail||{}), 예약확정방식: '010002' } }))}
                            />
                            <div className="w-5 h-5 border border-gray-300 rounded-full bg-white peer-checked:border-blue-500 transition-all"></div>
                            <div className="absolute top-1 left-1 w-3 h-3 rounded-full bg-blue-500 opacity-0 peer-checked:opacity-100 transition-opacity"></div>
                          </div>
                          <span className="text-sm font-medium text-gray-700">수동확정</span>
                        </label>
                      </div>
                    </FieldRow>
                    <FieldRow label="예약마감">
                      <div className="grid grid-cols-3 gap-2">
                        <input
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          className="col-span-2 block w-full px-4 py-2.5 border border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50 transition-all"
                          value={detailState.detail?.예약마감 ?? ''}
                          onKeyDown={(e)=>{
                            const allowed = ['Backspace','Delete','Tab','Enter','Escape','ArrowLeft','ArrowRight','Home','End'];
                            if (allowed.includes(e.key)) return;
                            if (e.ctrlKey || e.metaKey) return;
                            if (!/^[0-9]$/.test(e.key)) e.preventDefault();
                          }}
                          onChange={(e)=>{
                            const raw = e.target.value.replace(/[^0-9]/g, '');
                            setDetailState(prev=>({
                              ...prev,
                              detail: { ...(prev.detail||{}), 예약마감: raw === '' ? '' : Number(raw) }
                            }));
                          }}
                        />
                        <select
                          className="block w-full px-4 py-2.5 border border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50 transition-all appearance-none bg-white"
                          value={detailState.detail?.예약마감시간타입 ?? '012001'}
                          onChange={(e)=> setDetailState(prev=>({
                            ...prev,
                            detail: { ...(prev.detail||{}), 예약마감시간타입: e.target.value }
                          }))}
                          style={{ backgroundImage: "url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%23007CB2%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22%2F%3E%3C%2Fsvg%3E')", backgroundRepeat: "no-repeat", backgroundPosition: "right 0.7rem top 50%", backgroundSize: "0.65rem auto", paddingRight: "2rem" }}
                        >
                          <option value="012001">일 전 마감</option>
                          <option value="012002">시간 전 마감</option>
                        </select>
                      </div>
                    </FieldRow>
                    <FieldRow label="최소행사인원">
                      <input 
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        className="block w-full px-4 py-2.5 border border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50 transition-all" 
                        value={detailState.detail?.최소행사인원 ?? ''}
                        onKeyDown={(e)=>{
                          const allowed = ['Backspace','Delete','Tab','Enter','Escape','ArrowLeft','ArrowRight','Home','End'];
                          if (allowed.includes(e.key)) return;
                          if (e.ctrlKey || e.metaKey) return;
                          if (!/^[0-9]$/.test(e.key)) e.preventDefault();
                        }}
                        onChange={(e)=>{
                          const raw = e.target.value.replace(/[^0-9]/g, '');
                          setDetailState(prev=>({ ...prev, detail: { ...(prev.detail||{}), 최소행사인원: raw === '' ? '' : Number(raw) } }));
                        }} />
                    </FieldRow>
                  </div>
                </div>

                {/* 결제 설정 섹션 */}
                <div className="mt-6">
                  <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-100">
                    <div className="w-1 h-5 bg-emerald-500 rounded-full"></div>
                    <div className="text-sm font-semibold text-gray-800">결제 설정</div>
                  </div>
                  <div className="pl-3 space-y-2 text-sm">
                    <FieldRow label="현지 결제 여부">
                      <div className="flex items-center space-x-6" role="radiogroup" aria-label="현지지불코드">
                        <label className="inline-flex items-center space-x-2 cursor-pointer">
                          <div className="relative">
                            <input
                              type="radio"
                              className="sr-only peer"
                              name="localPay"
                              value={0}
                              checked={Number(detailState.detail?.현지지불코드) === 0}
                              onChange={()=> setDetailState(prev => ({ ...prev, detail: { ...(prev.detail||{}), 현지지불코드: 0 } }))}
                            />
                            <div className="w-5 h-5 border border-gray-300 rounded-full bg-white peer-checked:border-blue-500 transition-all"></div>
                            <div className="absolute top-1 left-1 w-3 h-3 rounded-full bg-blue-500 opacity-0 peer-checked:opacity-100 transition-opacity"></div>
                          </div>
                          <span className="text-sm font-medium text-gray-700">현지 결제 없음</span>
                        </label>
                        <label className="inline-flex items-center space-x-2 cursor-pointer">
                          <div className="relative">
                            <input
                              type="radio"
                              className="sr-only peer"
                              name="localPay"
                              value={1}
                              checked={Number(detailState.detail?.현지지불코드) === 1}
                              onChange={()=> setDetailState(prev => ({ ...prev, detail: { ...(prev.detail||{}), 현지지불코드: 1 } }))}
                            />
                            <div className="w-5 h-5 border border-gray-300 rounded-full bg-white peer-checked:border-blue-500 transition-all"></div>
                            <div className="absolute top-1 left-1 w-3 h-3 rounded-full bg-blue-500 opacity-0 peer-checked:opacity-100 transition-opacity"></div>
                          </div>
                          <span className="text-sm font-medium text-gray-700">현지 결제 있음</span>
                        </label>
                      </div>
                    </FieldRow>
                    <FieldRow label="신청금구분">
                      <select
                        className="block w-full px-3 py-2 border rounded"
                        value={detailState.detail?.신청금구분 ?? '004999'}
                        onChange={(e)=> setDetailState(prev => ({
                          ...prev,
                          detail: { ...(prev.detail||{}), 신청금구분: e.target.value }
                        }))}
                      >
                        <option value="004001">전체금액</option>
                        <option value="004002">판매총액(%)</option>
                        <option value="004003">1인당 고정금액(원)</option>
                        <option value="004004">판매당 고정금액(원)</option>
                        <option value="004999">없음</option>
                      </select>
                    </FieldRow>
                    {(['004002','004003','004004'].includes(detailState.detail?.신청금구분)) && (
                      <FieldRow label="신청금">
                        <div className="flex items-center space-x-2">
                          <input
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            className="block w-full px-3 py-2 border rounded"
                            value={(() => {
                              const t = detailState.detail?.신청금구분;
                              const v = detailState.detail?.신청금;
                              return ((t === '004003' || t === '004004') && v !== '' && v != null)
                                ? Number(v).toLocaleString('ko-KR')
                                : (v ?? '');
                            })()}
                            onKeyDown={(e)=>{
                              const allowed = ['Backspace','Delete','Tab','Enter','Escape','ArrowLeft','ArrowRight','Home','End'];
                              if (allowed.includes(e.key)) return;
                              if (e.ctrlKey || e.metaKey) return;
                              if (!/^[0-9]$/.test(e.key)) e.preventDefault();
                            }}
                            onChange={(e)=>{
                              const rawStr = e.target.value.replace(/[^0-9]/g, '');
                              const raw = Number(rawStr || 0);
                              const type = detailState.detail?.신청금구분;
                              let next = rawStr === '' ? '' : raw;
                              if (next !== '') {
                                if (type === '004002') {
                                  // percent: clamp 0..100
                                  next = Math.max(0, Math.min(100, raw));
                                } else if (type === '004003' || type === '004004') {
                                  // KRW: must be >= 0
                                  next = Math.max(0, raw);
                                }
                              }
                              setDetailState(prev => ({ ...prev, detail: { ...(prev.detail||{}), 신청금: next } }));
                            }}
                          />
                          <span className="text-sm text-gray-600">
                            {detailState.detail?.신청금구분 === '004002' ? '%' : '원'}
                          </span>
                        </div>
                      </FieldRow>
                    )}
                    <FieldRow label="완납기준일">
                      <select
                        className="block w-full px-3 py-2 border rounded"
                        value={detailState.detail?.완납기준일 ?? '205001'}
                        onChange={(e)=> setDetailState(prev => ({
                          ...prev,
                          detail: { ...(prev.detail||{}), 완납기준일: e.target.value }
                        }))}
                      >
                        <option value="205001">출발일</option>
                        <option value="205002">예약일</option>
                        <option value="205003">확정일</option>
                      </select>
                    </FieldRow>
                    <FieldRow label="완납시한">
                      <div className="flex items-center space-x-2">
                        <input
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          className="block w-24 px-3 py-2 border rounded text-right"
                          value={detailState.detail?.완납시한 ?? 0}
                          onKeyDown={(e)=>{
                            const allowed = ['Backspace','Delete','Tab','Enter','Escape','ArrowLeft','ArrowRight','Home','End'];
                            if (allowed.includes(e.key)) return;
                            if (e.ctrlKey || e.metaKey) return;
                            if (!/^[0-9]$/.test(e.key)) e.preventDefault();
                          }}
                          onChange={(e)=>{
                            const raw = e.target.value.replace(/[^0-9]/g, '');
                            const next = raw ? Math.max(0, parseInt(raw, 10)) : 0;
                            setDetailState(prev => ({ ...prev, detail: { ...(prev.detail||{}), 완납시한: next } }));
                          }}
                          placeholder="숫자"
                        />
                        <span className="text-sm text-gray-600 whitespace-nowrap">
                          {detailState.detail?.완납기준일 === '205001' ? '일 전까지' : '일 후까지'}
                        </span>
                      </div>
                    </FieldRow>
                  </div>
                </div>

                {/* 여행객 정보 섹션 */}
                <div className="mt-6">
                  <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-100">
                    <div className="w-1 h-5 bg-indigo-500 rounded-full"></div>
                    <div className="text-sm font-semibold text-gray-800">웹 예약 여행객 입력정보 설정 </div>
                  </div>
                  <div className="pl-3 space-y-2 text-sm">
                    <FieldRow label="여행자 정보">
                      <div className="flex items-center space-x-6" role="radiogroup" aria-label="대표승객여부">
                        <label className="inline-flex items-center space-x-2 cursor-pointer">
                          <div className="relative">
                            <input
                              type="radio"
                              className="sr-only peer"
                              name="leadPassengerOnly"
                              value="1"
                              checked={String(detailState.detail?.대표승객여부) === '1'}
                              onChange={() => setDetailState(prev => ({ ...prev, detail: { ...(prev.detail||{}), 대표승객여부: '1' } }))}
                            />
                            <div className="w-5 h-5 border border-gray-300 rounded-full bg-white peer-checked:border-blue-500 transition-all"></div>
                            <div className="absolute top-1 left-1 w-3 h-3 rounded-full bg-blue-500 opacity-0 peer-checked:opacity-100 transition-opacity"></div>
                          </div>
                          <span className="text-sm font-medium text-gray-700">대표자만 받음</span>
                        </label>
                        <label className="inline-flex items-center space-x-2 cursor-pointer">
                          <div className="relative">
                            <input
                              type="radio"
                              className="sr-only peer"
                              name="leadPassengerOnly"
                              value="0"
                              checked={String(detailState.detail?.대표승객여부) === '0'}
                              onChange={() => setDetailState(prev => ({ ...prev, detail: { ...(prev.detail||{}), 대표승객여부: '0' } }))}
                            />
                            <div className="w-5 h-5 border border-gray-300 rounded-full bg-white peer-checked:border-blue-500 transition-all"></div>
                            <div className="absolute top-1 left-1 w-3 h-3 rounded-full bg-blue-500 opacity-0 peer-checked:opacity-100 transition-opacity"></div>
                          </div>
                          <span className="text-sm font-medium text-gray-700">전체 여행객 정보만 받음</span>
                        </label>
                      </div>
                    </FieldRow>
                    <FieldRow label="여권정보 입력여부">
                      <div className="flex items-center space-x-6" role="radiogroup" aria-label="여권정보 입력여부">
                        <label className="inline-flex items-center space-x-2 cursor-pointer">
                          <div className="relative">
                            <input
                              type="radio"
                              className="sr-only peer"
                              name="passportInfoRequired"
                              value="true"
                              checked={!!detailState.detail?.여권사본필요여부 === true}
                              onChange={() => setDetailState(prev => ({ ...prev, detail: { ...(prev.detail||{}), 여권사본필요여부: true } }))}
                            />
                            <div className="w-5 h-5 border border-gray-300 rounded-full bg-white peer-checked:border-blue-500 transition-all"></div>
                            <div className="absolute top-1 left-1 w-3 h-3 rounded-full bg-blue-500 opacity-0 peer-checked:opacity-100 transition-opacity"></div>
                          </div>
                          <span className="text-sm font-medium text-gray-700">여권정보 필요</span>
                        </label>
                        <label className="inline-flex items-center space-x-2 cursor-pointer">
                          <div className="relative">
                            <input
                              type="radio"
                              className="sr-only peer"
                              name="passportInfoRequired"
                              value="false"
                              checked={!!detailState.detail?.여권사본필요여부 === false}
                              onChange={() => setDetailState(prev => ({ ...prev, detail: { ...(prev.detail||{}), 여권사본필요여부: false } }))}
                            />
                            <div className="w-5 h-5 border border-gray-300 rounded-full bg-white peer-checked:border-blue-500 transition-all"></div>
                            <div className="absolute top-1 left-1 w-3 h-3 rounded-full bg-blue-500 opacity-0 peer-checked:opacity-100 transition-opacity"></div>
                          </div>
                          <span className="text-sm font-medium text-gray-700">여권정보 불필요</span>
                        </label>
                      </div>
                    </FieldRow>
                    <FieldRow label="여권 발급일 입력 여부">
                      <div className="flex items-center space-x-6" role="radiogroup" aria-label="여권 발급일 입력 여부">
                        <label className="inline-flex items-center space-x-2 cursor-pointer">
                          <div className="relative">
                            <input
                              type="radio"
                              className="sr-only peer"
                              name="passportIssueRequired"
                              value="true"
                              checked={!!detailState.detail?.여권발급일필수여부 === true}
                              onChange={() => setDetailState(prev => ({ ...prev, detail: { ...(prev.detail||{}), 여권발급일필수여부: true } }))}
                            />
                            <div className="w-5 h-5 border border-gray-300 rounded-full bg-white peer-checked:border-blue-500 transition-all"></div>
                            <div className="absolute top-1 left-1 w-3 h-3 rounded-full bg-blue-500 opacity-0 peer-checked:opacity-100 transition-opacity"></div>
                          </div>
                          <span className="text-sm font-medium text-gray-700">여권 발급일 필요</span>
                        </label>
                        <label className="inline-flex items-center space-x-2 cursor-pointer">
                          <div className="relative">
                            <input
                              type="radio"
                              className="sr-only peer"
                              name="passportIssueRequired"
                              value="false"
                              checked={!!detailState.detail?.여권발급일필수여부 === false}
                              onChange={() => setDetailState(prev => ({ ...prev, detail: { ...(prev.detail||{}), 여권발급일필수여부: false } }))}
                            />
                            <div className="w-5 h-5 border border-gray-300 rounded-full bg-white peer-checked:border-blue-500 transition-all"></div>
                            <div className="absolute top-1 left-1 w-3 h-3 rounded-full bg-blue-500 opacity-0 peer-checked:opacity-100 transition-opacity"></div>
                          </div>
                          <span className="text-sm font-medium text-gray-700">여권 발급일 불필요</span>
                        </label>
                      </div>
                    </FieldRow>
                    <div className="pt-0">
                      <FieldRow label="연령구분 사용여부">
                        <div className="flex items-center space-x-6" role="radiogroup" aria-label="연령구분 사용여부">
                          <label className="inline-flex items-center space-x-2 cursor-pointer">
                            <div className="relative">
                              <input
                                type="radio"
                                className="sr-only peer"
                                name="ageGroupUsed"
                                value="true"
                                checked={detailState.detail?.연령구분여부 !== false}
                                onChange={() => setDetailState(prev => ({ ...prev, detail: { ...(prev.detail||{}), 연령구분여부: true } }))}
                              />
                              <div className="w-5 h-5 border border-gray-300 rounded-full bg-white peer-checked:border-blue-500 transition-all"></div>
                              <div className="absolute top-1 left-1 w-3 h-3 rounded-full bg-blue-500 opacity-0 peer-checked:opacity-100 transition-opacity"></div>
                            </div>
                            <span className="text-sm font-medium text-gray-700">연령구분 사용</span>
                          </label>
                          <label className="inline-flex items-center space-x-2 cursor-pointer">
                            <div className="relative">
                              <input
                                type="radio"
                                className="sr-only peer"
                                name="ageGroupUsed"
                                value="false"
                                checked={detailState.detail?.연령구분여부 === false}
                                onChange={() => setDetailState(prev => ({ ...prev, detail: { ...(prev.detail||{}), 연령구분여부: false } }))}
                              />
                              <div className="w-5 h-5 border border-gray-300 rounded-full bg-white peer-checked:border-blue-500 transition-all"></div>
                              <div className="absolute top-1 left-1 w-3 h-3 rounded-full bg-blue-500 opacity-0 peer-checked:opacity-100 transition-opacity"></div>
                            </div>
                            <span className="text-sm font-medium text-gray-700">연령구분 미사용</span>
                          </label>
                        </div>
                      </FieldRow>
                      {detailState.detail?.연령구분여부 !== false && (
                        <>
                          <FieldRow label="유아 나이">
                            <div className="flex items-center space-x-2">
                              <input
                                type="number"
                                disabled
                                className="w-24 px-3 py-2 border rounded bg-gray-100 text-gray-600"
                                value={0}
                              />
                              <span>~</span>
                              <input
                                type="text"
                                inputMode="numeric"
                                pattern="[0-9]*"
                                className="w-24 px-3 py-2 border rounded"
                                value={detailState.detail?.연령유아끝 ?? 3}
                                onKeyDown={(e)=>{
                                  const allowed = ['Backspace','Delete','Tab','Enter','Escape','ArrowLeft','ArrowRight','Home','End'];
                                  if (allowed.includes(e.key)) return;
                                  if (e.ctrlKey || e.metaKey) return;
                                  if (!/^[0-9]$/.test(e.key)) e.preventDefault();
                                }}
                                onChange={(e)=>{
                                  const rawStr = e.target.value.replace(/[^0-9]/g, '');
                                  const end = Math.max(0, Number(rawStr||0));
                                  setDetailState(prev=>({
                                    ...prev,
                                    detail: {
                                      ...(prev.detail||{}),
                                      연령유아끝: rawStr === '' ? '' : end,
                                      연령소아시작: rawStr === '' ? prev.detail?.연령소아시작 : end // sync
                                    }
                                  }));
                                }}
                              />
                              <span className="text-sm text-gray-600">세</span>
                            </div>
                          </FieldRow>
                          <FieldRow label="소아 나이">
                            <div className="flex items-center space-x-2">
                              <input
                                type="number"
                                disabled
                                className="w-24 px-3 py-2 border rounded bg-gray-100 text-gray-600"
                                value={detailState.detail?.연령소아시작 ?? (detailState.detail?.연령유아끝 ?? 3)}
                              />
                              <span>~</span>
                              <input
                                type="text"
                                inputMode="numeric"
                                pattern="[0-9]*"
                                className="w-24 px-3 py-2 border rounded"
                                value={detailState.detail?.연령소아끝 ?? 12}
                                onKeyDown={(e)=>{
                                  const allowed = ['Backspace','Delete','Tab','Enter','Escape','ArrowLeft','ArrowRight','Home','End'];
                                  if (allowed.includes(e.key)) return;
                                  if (e.ctrlKey || e.metaKey) return;
                                  if (!/^[0-9]$/.test(e.key)) e.preventDefault();
                                }}
                                onChange={(e)=>{
                                  const rawStr = e.target.value.replace(/[^0-9]/g, '');
                                  const end = Math.max(0, Number(rawStr||0));
                                  setDetailState(prev=>({
                                    ...prev,
                                    detail: {
                                      ...(prev.detail||{}),
                                      연령소아끝: rawStr === '' ? '' : end,
                                      연령성인시작: rawStr === '' ? prev.detail?.연령성인시작 : end // sync
                                    }
                                  }));
                                }}
                              />
                              <span className="text-sm text-gray-600">세</span>
                            </div>
                          </FieldRow>
                          <FieldRow label="성인 나이">
                            <div className="flex items-center space-x-2">
                              <input
                                type="number"
                                disabled
                                className="w-24 px-3 py-2 border rounded bg-gray-100 text-gray-600"
                                value={detailState.detail?.연령성인시작 ?? (detailState.detail?.연령소아끝 ?? 12)}
                              />
                              <span className="text-sm text-gray-600">세 이상</span>
                            </div>
                          </FieldRow>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex justify-end">
                  <button
                    disabled={savingDetail}
                    onClick={async ()=>{
                      try {
                        setSavingDetail(true);
                        const sanitizedFields = (detailState.requestFields || []).map(f => {
                          const out = {
                            ...f,
                            필수여부: f.표시여부 ? true : !!f.필수여부,
                          };
                          // 리스트 타입일 때 옵션목록은 배열 보장
                          if (out.데이터타입 === '리스트') {
                            out.옵션목록 = Array.isArray(out.옵션목록) ? out.옵션목록 : [];
                          }
                          // 전송 불필요한 임시 입력값 제거
                          delete out._newOption;
                          return out;
                        });
                        const visibleOnly = sanitizedFields.filter(x => !!x.표시여부);
                        // 저장 전 타입 정규화
                        const normalizedDetail = {
                          ...(detailState.detail || {}),
                          현지지불코드: String(detailState.detail?.현지지불코드 ?? '0'),
                          신청금: String(detailState.detail?.신청금 ?? '100'),
                          연령성인끝: detailState.detail?.연령성인끝 ?? 100,
                        };
                        await saveProductDetailExtra({ productNo: product.상품번호, detail: normalizedDetail, requestFields: visibleOnly });
                        // 상세 저장 성공 시 스냅샷 갱신
                        lastSavedDetailRef.current = detailState.detail || {};
                        // 부모 미리보기 새로고침
                        refreshPreview();
                        window.alert('상세 정보가 저장되었습니다.');
                      } catch (e) {
                        console.error('상세 정보 저장 실패:', e);
                        window.alert('상세 정보 저장에 실패했습니다. 다시 시도해 주세요.');
                      } finally {
                        setSavingDetail(false);
                      }
                    }}
                    className={`px-5 py-2 rounded-md font-medium ${savingDetail ? 'bg-gray-300 text-gray-600' : 'bg-primary-600 text-white hover:bg-blue-700'}`}
                  >
                    {savingDetail ? '저장 중...' : '상세 정보 저장'}
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {activeTab === 'request' && (
          <div className="space-y-4">
            {detailState.loading && <div className="text-sm text-gray-500">필드를 불러오는 중...</div>}
            {detailState.error && <div className="text-sm text-red-500">{detailState.error}</div>}
            {!detailState.loading && (
              <>
                {/* 예약시 받을 정보 섹션 */}
                <div>
                  <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-100">
                    <div className="flex items-center gap-2">
                      <div className="w-1 h-5 bg-blue-500 rounded-full"></div>
                      <div className="text-sm font-semibold text-gray-800">예약시 받을 정보</div>
                    </div>
                    <button
                      type="button"
                      className="px-3 py-1.5 text-xs rounded-md bg-gray-700 hover:bg-gray-800 text-white font-medium"
                      onClick={() => {
                        const order = (detailState.requestFields?.length || 0) + 1;
                        const newField = {
                          관리주체: '업체',
                          표시여부: true,
                          필수여부: true,
                          필드라벨: '',
                          데이터타입: '텍스트',
                          옵션목록: [],
                          노출순서: order,
                        };
                        const next = [...(detailState.requestFields || []), newField];
                        setDetailState(prev => ({ ...prev, requestFields: next }));
                        // 즉시 서버에 반영
                        persistRequestField(newField);
                      }}
                    >
                      + 필드 추가
                    </button>
                  </div>
                  <div className="pl-3">
                    {detailState.requestFields.length === 0 && (
                      <div className="border border-dashed rounded-lg p-6 text-center text-sm text-gray-500">
                        아직 추가된 필드가 없습니다. "필드 추가" 버튼으로 첫 필드를 만들어 보세요.
                      </div>
                    )}
                    <div className="space-y-3">
                      {detailState.requestFields.map((f, idx) => (
                        <div key={f.필드번호 || idx} className="rounded-lg border border-gray-200 bg-white shadow-sm p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              {f.관리주체 === '업체' ? (
                                <input
                                  type="text"
                                  className="w-full border rounded-md px-3 py-2 font-medium text-gray-900 truncate"
                                  value={f.필드라벨 || ''}
                                  onChange={(e)=>{
                                    const updated = { ...f, 필드라벨: e.target.value };
                                    const next = [...detailState.requestFields];
                                    next[idx] = updated;
                                    setDetailState(prev=>({ ...prev, requestFields: next }));
                                  }}
                                  onBlur={() => {
                                    const latest = (detailState.requestFields || [])[idx];
                                    if (latest) {
                                      persistRequestField(latest);
                                    }
                                  }}
                                  placeholder="필드라벨 입력"
                                />
                              ) : (
                                <div className="w-full border rounded-md px-3 py-2 font-medium text-gray-900 truncate">{f.필드라벨 || '필드라벨 없음'}</div>
                              )}
                              <div className="mt-2 flex items-center flex-wrap gap-2 text-xs">
                                <span className="inline-flex items-center px-2 py-0.5 rounded bg-gray-100 text-gray-700 border">관리주체: {f.관리주체}</span>
                                <span className="inline-flex items-center px-2 py-0.5 rounded bg-gray-100 text-gray-700 border">데이터타입: {f.데이터타입}</span>
                                {f.데이터타입 === '리스트' && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200">항목 {Array.isArray(f.옵션목록) ? f.옵션목록.length : 0}개</span>
                                )}
                              </div>
                            </div>
                            <div className="ml-4 flex items-center space-x-4">
                              <label className="flex items-center text-sm text-gray-600 cursor-pointer select-none">
                                <div className="relative mr-2">
                                  <input
                                    type="checkbox"
                                    className="sr-only peer"
                                    checked={!!f.표시여부}
                                    onChange={async (e)=>{
                                      const val = e.target.checked;
                                      const updated = { ...f, 표시여부: val, 필수여부: val ? true : false };
                                      const next = [...detailState.requestFields];
                                      next[idx] = updated;
                                      setDetailState(prev=>({ ...prev, requestFields: next }));
                                      persistRequestField(updated);
                                      try {
                                        await saveProductDetailExtra({
                                          productNo: product.상품번호,
                                          detail: lastSavedDetailRef.current || {},
                                          requestFields: next.filter(x => !!x.표시여부),
                                        });
                                        // 부모 미리보기 새로고침
                                        refreshPreview();
                                        showAutoSaved();
                                      } catch (err) {
                                        console.error('표시여부 변경 저장 실패:', err);
                                      }
                                    }}
                                  />
                                  <div className="w-9 h-5 rounded-full bg-gray-200 peer-checked:bg-blue-500 transition-all"></div>
                                  <div className="absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transform transition-all peer-checked:translate-x-4"></div>
                                </div>
                                표시
                              </label>
                              {f.관리주체 === '업체' && (
                                <button
                                  type="button"
                                  className="px-3 py-1 text-sm rounded-md border border-red-200 text-red-600 hover:bg-red-50"
                                  onClick={async () => {
                                    const ok = window.confirm('해당 필드를 삭제하시겠습니까?');
                                    if (!ok) return;
                                    try {
                                      if (f.필드번호) {
                                        await deleteRequestField({ fieldNo: f.필드번호 });
                                      }
                                      const next = detailState.requestFields.filter((_, i) => i !== idx);
                                      setDetailState(prev => ({ ...prev, requestFields: next }));
                                      try {
                                        await saveProductDetailExtra({
                                          productNo: product.상품번호,
                                          detail: lastSavedDetailRef.current || {},
                                          requestFields: next.filter(x => !!x.표시여부),
                                        });
                                        showAutoSaved('삭제가 저장되었습니다.');
                                      } catch (syncErr) {
                                        console.error('삭제 후 동기화 저장 실패:', syncErr);
                                      }
                                      window.alert('삭제되었습니다.');
                                    } catch (e) {
                                      console.error('필드 삭제 실패:', e);
                                      window.alert('삭제에 실패했습니다. 다시 시도해 주세요.');
                                    }
                                  }}
                                >삭제</button>
                              )}
                            </div>
                          </div>
                          <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                            <div>
                              <div className="text-gray-500 mb-1">데이터타입</div>
                              {f.관리주체 === '업체' ? (
                                <select
                                  className="w-full border px-3 py-2 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  value={f.데이터타입 || '텍스트'}
                                  onChange={(e)=>{
                                    const val = e.target.value;
                                    const updated = { ...f, 데이터타입: val, 옵션목록: val === '리스트' ? (Array.isArray(f.옵션목록) ? f.옵션목록 : []) : f.옵션목록 };
                                    const next = [...detailState.requestFields];
                                    next[idx] = updated;
                                    setDetailState(prev=>({ ...prev, requestFields: next }));
                                    persistRequestField(updated);
                                  }}
                                >
                                  <option value="텍스트">텍스트</option>
                                  <option value="리스트">리스트</option>
                                  <option value="체크박스">체크박스</option>
                                </select>
                              ) : (
                                <div className="text-gray-800">{f.데이터타입}</div>
                              )}
                            </div>
                            <div>
                              <div className="text-gray-500 mb-1">관리주체</div>
                              <div className="text-gray-800">{f.관리주체}</div>
                            </div>
                          </div>
                          {f.데이터타입 === '리스트' && (
                            <div className="mt-4 text-sm">
                              <div className="text-gray-500 mb-2">리스트 항목</div>
                              <div className="flex items-center gap-2">
                                <input
                                  type="text"
                                  className="flex-1 border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  value={f._newOption || ''}
                                  onChange={(e)=>{
                                    const next = [...detailState.requestFields];
                                    next[idx] = { ...f, _newOption: e.target.value };
                                    setDetailState(prev=>({ ...prev, requestFields: next }));
                                  }}
                                  placeholder="항목 입력 후 추가"
                                  onKeyDown={(e)=>{
                                    if (e.key === 'Enter') {
                                      const text = (f._newOption || '').trim();
                                      if (!text) return;
                                      const updated = { ...f, _newOption: '', 옵션목록: [...(Array.isArray(f.옵션목록) ? f.옵션목록 : []), text] };
                                      const next = [...detailState.requestFields];
                                      next[idx] = updated;
                                       setDetailState(prev=>({ ...prev, requestFields: next }));
                                      persistRequestField(updated);
                                    }
                                  }}
                                  disabled={f.관리주체 !== '업체'}
                                />
                                <button
                                  type="button"
                                  className="px-3 py-2 rounded-md bg-gray-100 hover:bg-gray-200"
                                  onClick={()=>{
                                    const text = (f._newOption || '').trim();
                                    if (!text) return;
                                    const updated = { ...f, _newOption: '', 옵션목록: [...(Array.isArray(f.옵션목록) ? f.옵션목록 : []), text] };
                                    const next = [...detailState.requestFields];
                                    next[idx] = updated;
                                    setDetailState(prev=>({ ...prev, requestFields: next }));
                                    persistRequestField(updated);
                                  }}
                                  disabled={f.관리주체 !== '업체'}
                                >추가</button>
                              </div>
                              <div className="mt-3 flex flex-wrap gap-2">
                                {(Array.isArray(f.옵션목록) ? f.옵션목록 : []).map((opt, oi) => (
                                  <span key={oi} className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-700 border">
                                    {opt}
                                    {f.관리주체 === '업체' && (
                                      <button
                                        type="button"
                                        className="ml-1 text-gray-500 hover:text-red-600"
                                        onClick={()=>{
                                          const updated = { ...f, 옵션목록: (Array.isArray(f.옵션목록) ? f.옵션목록 : []).filter((_, i)=> i !== oi) };
                                          const next = [...detailState.requestFields];
                                          next[idx] = updated;
                                          setDetailState(prev=>({ ...prev, requestFields: next }));
                                          persistRequestField(updated);
                                        }}
                                      >×</button>
                                    )}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* 수동 저장 버튼 제거됨 - 변경 즉시 자동 저장 */}
              </>
            )}
          </div>
        )}

        {activeTab === 'basic' && (
          <div className="mt-8">
            <div className="sticky bottom-0 left-0 right-0 z-10 bg-white/90 backdrop-blur px-3 py-3">
              <div className="flex justify-between items-center">
                <div>
                  <button 
                    onClick={openCopyModal}
                    className="px-5 py-2 bg-gray-100 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-200 transition-colors font-medium flex items-center"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M7 9a2 2 0 012-2h6a2 2 0 012 2v6a2 2 0 01-2 2H9a2 2 0 01-2-2V9z" />
                      <path d="M5 3a2 2 0 00-2 2v6a2 2 0 002 2V5h8a2 2 0 00-2-2H5z" />
                    </svg>
                    상품 복사
                  </button>
                </div>
                <div className="flex space-x-3">
                  <button 
                    onClick={onClose} 
                    className="px-5 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors font-medium"
                  >
                    취소
                  </button>
                  <button 
                    onClick={async () => {
                      const prevName = (product?.상품명 ?? '').toString().trim();
                      const nextName = (formData?.상품명 ?? '').toString().trim();
                      const productNo = formData?.상품번호 ?? product?.상품번호;
                      const nameChanged = !!nextName && prevName !== nextName;
                      try {
                        const res = await (typeof onSave === 'function' ? onSave(formData) : false);
                        const ok = res === true;
                        const alreadyOnPriceStock = String(location?.pathname || '').startsWith('/price-stock/');
                        if (ok && nameChanged && productNo && !alreadyOnPriceStock) {
                          navigate(`/price-stock/${productNo}`, {
                            state: {
                              autoSyncPriceName: true,
                              newProductName: nextName,
                              from: location?.pathname || '',
                            },
                          });
                        }
                      } catch (_) {
                        // onSave handles its own error UI
                      }
                    }}
                    className="px-5 py-2 bg-primary-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium"
                  >
                    저장
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showCopyModal && hasDOM && ReactDOM.createPortal(
          (
            <div className="fixed inset-0" style={{ zIndex: 9999 }} onMouseDown={() => setShowCopyModal(false)}>
              <div className="absolute inset-0 bg-black bg-opacity-50" />
              <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="copy-modal-title"
                className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-2xl w-[560px] max-w-[92vw]"
                onMouseDown={(e) => e.stopPropagation()}
              >
                <div className="px-5 py-4 border-b flex items-center justify-between">
                  <div>
                    <h3 id="copy-modal-title" className="text-lg font-semibold text-gray-900">상품 복사</h3>
                    <p className="text-xs text-gray-500 mt-0.5">원본 상품을 기반으로 새 상품을 생성합니다. 필수 항목을 입력 후 복사 실행을 눌러주세요.</p>
                  </div>
                  <button
                    type="button"
                    className="text-gray-400 hover:text-gray-600"
                    onClick={() => setShowCopyModal(false)}
                  >
                    <span className="sr-only">닫기</span>
                    ×
                  </button>
                </div>
                <div className="px-5 py-4 space-y-4">
                  {/* 원본 상품명 */}
                  <div>
                    <div className="text-xs text-gray-500 mb-1">원본 상품명</div>
                    <div className="px-3 py-2 border border-gray-200 rounded bg-gray-50 text-sm text-gray-800">{product?.상품명 ?? '-'}</div>
                  </div>
                  {/* 새 상품명 */}
                  <div>
                    <label htmlFor="copy-name-input" className="block text-sm font-medium text-gray-700">새 상품명 <span className="text-red-500">*</span></label>
                    <input
                      id="copy-name-input"
                      ref={nameInputRef}
                      className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50 ${copyErrors.name ? 'border-red-400' : 'border-gray-300'}`}
                      value={copyForm.name}
                      onChange={(e) => setCopyForm((prev) => ({ ...prev, name: e.target.value }))}
                      placeholder="복사될 상품명을 입력하세요"
                    />
                    {copyErrors.name && <p className="mt-1 text-xs text-red-600">{copyErrors.name}</p>}
                  </div>
                  {/* 통화코드 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700">통화코드 <span className="text-red-500">*</span></label>
                    <select
                      className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50 bg-white ${copyErrors.currencyCode ? 'border-red-400' : 'border-gray-300'}`}
                      value={copyForm.currencyCode}
                      onChange={(e) => setCopyForm((prev) => ({ ...prev, currencyCode: e.target.value }))}
                      disabled={currencyLoading}
                    >
                      {currencyLoading && <option>로딩 중...</option>}
                      {!currencyLoading && currencyOptions.length === 0 && <option>통화코드 없음</option>}
                      {!currencyLoading && currencyOptions.map((c) => (
                        <option key={String(c.코드)} value={String(c.코드)}>{c.코드명}</option>
                      ))}
                    </select>
                    {copyErrors.currencyCode && <p className="mt-1 text-xs text-red-600">{copyErrors.currencyCode}</p>}
                  </div>
                  {/* 소요시간 + 타입 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700">투어 소요시간 <span className="text-red-500">*</span></label>
                    <div className="mt-1 grid grid-cols-3 gap-2">
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        className={`col-span-2 block w-full px-3 py-2 border rounded-md shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50 ${copyErrors.duration ? 'border-red-400' : 'border-gray-300'}`}
                        value={copyForm.duration}
                        onKeyDown={(e)=>{
                          const allowed = ['Backspace','Delete','Tab','Enter','Escape','ArrowLeft','ArrowRight','Home','End'];
                          if (allowed.includes(e.key)) return;
                          if (e.ctrlKey || e.metaKey) return;
                          if (!/^[0-9]$/.test(e.key)) e.preventDefault();
                        }}
                        onChange={(e)=>{
                          const raw = e.target.value.replace(/[^0-9]/g, '');
                          setCopyForm((prev)=>({ ...prev, duration: raw }));
                        }}
                        placeholder="숫자만 입력"
                      />
                      <select
                        className={`block w-full px-3 py-2 border rounded-md shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200 focus:ring-opacity-50 bg-white ${copyErrors.durationType ? 'border-red-400' : 'border-gray-300'}`}
                        value={copyForm.durationType}
                        onChange={(e)=> setCopyForm((prev)=>({ ...prev, durationType: e.target.value }))}
                      >
                        <option value="012001">일</option>
                        <option value="012002">시간</option>
                      </select>
                    </div>
                    {copyErrors.duration && <p className="mt-1 text-xs text-red-600">{copyErrors.duration}</p>}
                    {copyErrors.durationType && <p className="mt-1 text-xs text-red-600">{copyErrors.durationType}</p>}
                    <p className="mt-1 text-[11px] text-gray-500">예: 6시간이면 소요시간 6, 소요시간타입은 "시간"을 선택하세요.</p>
                  </div>
                  {/* 복사 후 이동 옵션 */}
                  <div>
                    <div className="block text-sm font-medium text-gray-700">복사 완료 후 이동</div>
                    <div className="mt-1 flex items-center gap-4 text-sm text-gray-700">
                      <label className="inline-flex items-center gap-2 cursor-pointer">
                        <input type="radio" name="afterCopyNav" value="none" checked={navigateChoice==='none'} onChange={()=>setNavigateChoice('none')} />
                        <span>이동하지 않음</span>
                      </label>
                      <label className="inline-flex items-center gap-2 cursor-pointer">
                        <input type="radio" name="afterCopyNav" value="detail" checked={navigateChoice==='detail'} onChange={()=>setNavigateChoice('detail')} />
                        <span>상세 설정 페이지</span>
                      </label>
                      <label className="inline-flex items-center gap-2 cursor-pointer">
                        <input type="radio" name="afterCopyNav" value="price" checked={navigateChoice==='price'} onChange={()=>setNavigateChoice('price')} />
                        <span>가격/재고 페이지</span>
                      </label>
                    </div>
                    <p className="mt-1 text-[11px] text-gray-500">새 상품번호를 기준으로 해당 페이지로 이동합니다.</p>
                  </div>
                </div>
                <div className="px-5 py-4 border-t flex items-center justify-end gap-2 bg-gray-50 rounded-b-lg">
                  <button
                    type="button"
                    className="px-4 py-2 text-sm rounded border border-gray-300 text-gray-700 hover:bg-gray-100"
                    onClick={() => setShowCopyModal(false)}
                  >
                    닫기
                  </button>
                  <button
                    type="button"
                    className={`px-4 py-2 text-sm rounded ${copyLoading ? 'bg-gray-300' : 'bg-blue-600 hover:bg-blue-700'} text-white`}
                    disabled={copyLoading || currencyLoading}
                    onClick={handleCopySubmit}
                  >
                    {copyLoading ? '복사 중...' : '복사'}
                  </button>
                </div>
              </div>
            </div>
          ), document.body)
        }
      </div>
    </div>
  );
};

// 공통 필드 행: 좌측 라벨, 우측 컨트롤(한 줄) + 우측 액션 슬롯
const FieldRow = ({ label, children, rightAction = null }) => (
  <div className="flex items-center py-1.5 gap-2">
    <span className="text-gray-500 shrink-0 w-36 text-sm">{label}</span>
    <div className="flex-1 flex items-center">
      <div className="flex-1">{children}</div>
      {rightAction && <div className="ml-2">{rightAction}</div>}
    </div>
  </div>
);

// 인라인 아이콘 버튼 (연필 아이콘)
const IconButton = ({ ariaLabel, onClick }) => (
  <button
    type="button"
    aria-label={ariaLabel}
    onClick={onClick}
    className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
  >
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
      <path d="M13.586 3.586a2 2 0 112.828 2.828l-8.486 8.486a2 2 0 01-.878.516l-3.09.772a.5.5 0 01-.606-.606l.772-3.09a2 2 0 01.516-.878l8.486-8.486z" />
      <path d="M12 5l3 3" />
    </svg>
  </button>
);

// 토글 스위치 컴포넌트
const Switch = ({ checked, onChange, onLabel = 'ON', offLabel = 'OFF', disabled = false }) => {
  const toggle = () => {
    if (disabled) return;
    onChange(!checked);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggle();
    }
  };

  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label="toggle"
      disabled={disabled}
      onClick={toggle}
      onKeyDown={onKeyDown}
      className={`relative inline-flex items-center h-6 w-11 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${checked ? 'bg-blue-600' : 'bg-gray-300'} ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ${checked ? 'translate-x-5' : 'translate-x-1'}`}
      />
      <span className="sr-only">{checked ? onLabel : offLabel}</span>
    </button>
  );
};

const InputField = (props) => (
  <input 
    {...props} 
    className="block w-full px-3 py-2 bg-white border border-gray-300 rounded-md placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm" 
  />
);

const DropdownField = ({ name, value, options, isOpen, onToggle, onChange }) => {
  const wrapRef = useRef(null);

  useEffect(() => {
    const onDocMouseDown = (e) => {
      if (!isOpen) return;
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        onToggle(); // close
      }
    };
    const onDocKeyDown = (e) => {
      if (!isOpen) return;
      if (e.key === 'Escape') {
        onToggle();
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onDocKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onDocKeyDown);
    };
  }, [isOpen, onToggle]);

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={onToggle}
        className="flex justify-between items-center w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-left focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm"
      >
        <span className={value ? 'text-gray-900' : 'text-gray-400'}>
          {value || '선택하세요'}
        </span>
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} 
          viewBox="0 0 20 20" 
          fill="currentColor" 
          aria-hidden="true"
        >
          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      </button>
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-60 py-1 overflow-auto focus:outline-none">
          {options.map((option) => (
            <div
              key={option}
              onClick={() => onChange(option)}
              className={`cursor-pointer select-none relative py-2 px-3 hover:bg-gray-50 ${
                value === option 
                  ? 'bg-blue-50 text-blue-700' 
                  : 'text-gray-900'
              }`}
            >
              {option}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProductSettingsModal;

// ===== Hangul 초성 추출 및 매칭 유틸 =====
const CHO = [
  'ㄱ','ㄲ','ㄴ','ㄷ','ㄸ','ㄹ','ㅁ','ㅂ','ㅃ','ㅅ','ㅆ','ㅇ','ㅈ','ㅉ','ㅊ','ㅋ','ㅌ','ㅍ','ㅎ'
];
const HANGUL_BASE = 0xac00;
const HANGUL_LAST = 0xd7a3;
const CHOSUNG_CYCLE = 588; // 21 * 28

const toChosung = (str = '') => {
  let res = '';
  for (let i = 0; i < str.length; i++) {
    const ch = str.charCodeAt(i);
    if (ch >= HANGUL_BASE && ch <= HANGUL_LAST) {
      const idx = Math.floor((ch - HANGUL_BASE) / CHOSUNG_CYCLE);
      res += CHO[idx] || str[i];
    } else {
      res += str[i];
    }
  }
  return res;
};

const matchKorean = (query, text) => {
  if (!query) return true;
  const q = query.trim();
  const t = text || '';
  // 일반 포함 검색
  if (t.includes(q)) return true;
  // 초성 검색
  const tCho = toChosung(t);
  return tCho.includes(q);
};

// ===== 도시 선택 컴포넌트 (초성 검색 + SP 호출) =====
const CitySelector = ({ value, isOpen, onToggle, onChange }) => {
  const [cities, setCities] = useState([]); // [{코드, 코드명}]
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const wrapRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    const fetchCities = async () => {
      try {
        setLoading(true);
        setError('');
        const data = await getCities();
        if (!mounted) return;
        // API returns data.table with keys: 코드, 코드명
        setCities(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!mounted) return;
        setError('카테고리 목록을 불러오는 중 오류가 발생했습니다.');
      } finally {
        if (mounted) setLoading(false);
      }
    };
    // 최초 1회 로드 (열릴 때 로드하려면 isOpen 의존성 사용 가능)
    if (cities.length === 0) fetchCities();
    return () => { mounted = false; };
  }, []);

  // Outside click & ESC close
  useEffect(() => {
    const onDocMouseDown = (e) => {
      if (!isOpen) return;
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        onToggle();
      }
    };
    const onDocKeyDown = (e) => {
      if (!isOpen) return;
      if (e.key === 'Escape') {
        onToggle();
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onDocKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onDocKeyDown);
    };
  }, [isOpen, onToggle]);

  const filtered = cities.filter((c) => matchKorean(query, c.코드명));

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={onToggle}
        className="flex justify-between items-center w-full px-3 py-2 bg-white text-left focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
      >
        <span className={value ? 'text-gray-900' : 'text-gray-400'}>{value || '선택하세요'}</span>
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      </button>
      {isOpen && (
        <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-80 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
          <div className="px-2 py-2 sticky top-0 bg-white border-b z-20">
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="카테고리명 또는 초성(ㄷㅅ 등)으로 검색"
              className="w-full px-2 py-1.5 border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
          {loading && <div className="px-3 py-2 text-sm text-gray-500">불러오는 중...</div>}
          {error && <div className="px-3 py-2 text-sm text-red-500">{error}</div>}
          {!loading && filtered.length === 0 && (
            <div className="px-3 py-2 text-sm text-gray-500">검색 결과가 없습니다.</div>
          )}
          {!loading && filtered.map((c) => (
            <div
              key={c.코드}
              onClick={() => {
                onChange(c.코드명); // UI에는 코드명만 노출 및 저장
              }}
              className={`cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-blue-100 ${value === c.코드명 ? 'bg-blue-50 text-blue-700' : 'text-gray-900'}`}
            >
              {c.코드명}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

